// This file contains the logic for real-time chord recognition from MIDI input.

const SHARP_NOTES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];

const INTERVALS_TO_CHORD: { [key: string]: { name: string, quality: string } } = {
    // Triads
    '0,3,7': { name: 'm', quality: 'Minor' },
    '0,4,7': { name: '', quality: 'Major' },
    '0,3,6': { name: '°', quality: 'Diminished' },
    '0,4,8': { name: '+', quality: 'Augmented' },
    '0,5,7': { name: 'sus4', quality: 'Suspended Fourth' },
    '0,2,7': { name: 'sus2', quality: 'Suspended Second' },
    // Sixths
    '0,4,7,9': { name: '6', quality: 'Major Sixth' },
    '0,3,7,9': { name: 'm6', quality: 'Minor Sixth' },
    // Sevenths
    '0,4,7,11': { name: 'maj7', quality: 'Major Seventh' },
    '0,3,7,10': { name: 'm7', quality: 'Minor Seventh' },
    '0,4,7,10': { name: '7', quality: 'Dominant Seventh' },
    '0,3,6,10': { name: 'ø7', quality: 'Half-Diminished Seventh' },
    '0,3,6,9': { name: '°7', quality: 'Diminished Seventh' },
    '0,3,7,11': { name: 'm(maj7)', quality: 'Minor-Major Seventh' },
    '0,4,8,10': { name: '7♯5', quality: 'Augmented Dominant Seventh' },
    '0,5,7,10': { name: '7sus4', quality: 'Suspended Dominant Seventh' },
    // Ninths & Extensions (Normalized to a single octave)
    '0,2,4,7': { name: 'add9', quality: 'Add Ninth' },
    '0,2,4,7,9': { name: '6/9', quality: 'Six Nine' },
    '0,2,4,7,11': { name: 'maj9', quality: 'Major Ninth' },
    '0,2,3,7,10': { name: 'm9', quality: 'Minor Ninth' },
    '0,2,4,7,10': { name: '9', quality: 'Dominant Ninth' },
    '0,1,4,7,10': { name: '7b9', quality: 'Dominant Flat Ninth' },
    '0,3,4,7,10': { name: '7#9', quality: 'Dominant Sharp Ninth' },
};


/**
 * Analyzes an array of MIDI note numbers and returns the recognized chord string.
 * This version uses a two-pass system. First, it tries to find a single chord
 * that fits all the notes. If that fails, it assumes the lowest note is a
 * separate bass note and analyzes the remaining notes as an "upper structure"
 * to identify complex slash chords (e.g., C/D).
 * @param midiNotes - An array of MIDI note numbers (e.g., [60, 64, 67]).
 * @returns A string representing the chord (e.g., "C", "Am7", "G7/B"), or null if not recognized.
 */
export const notesToChordString = (midiNotes: number[]): string | null => {
    if (midiNotes.length < 2) return null;

    const sortedNotes = [...midiNotes].sort((a, b) => a - b);
    const bassNoteNumber = sortedNotes[0];
    const bassNoteName = SHARP_NOTES[bassNoteNumber % 12];
    
    // --- Pass 1: Full chord analysis ---
    const pitchClasses = [...new Set(sortedNotes.map(note => note % 12))].sort((a, b) => a - b);
    const possibleMatches: { root: string, quality: string, inversion: boolean }[] = [];

    for (let i = 0; i < pitchClasses.length; i++) {
        const potentialRoot = pitchClasses[i];
        const intervals = pitchClasses.map(pc => (pc - potentialRoot + 12) % 12).sort((a, b) => a - b);
        const intervalKey = intervals.join(',');

        if (INTERVALS_TO_CHORD[intervalKey]) {
            const rootNoteName = SHARP_NOTES[potentialRoot];
            const chordQuality = INTERVALS_TO_CHORD[intervalKey].name;
            possibleMatches.push({ 
                root: rootNoteName, 
                quality: chordQuality, 
                inversion: rootNoteName !== bassNoteName 
            });
        }
    }

    // --- NEW DECISION LOGIC ---
    // Rule 1: A root-position chord that explains all notes is the strongest match.
    const rootPositionMatch = possibleMatches.find(match => !match.inversion);
    if (rootPositionMatch) {
        return `${rootPositionMatch.root}${rootPositionMatch.quality}`;
    }

    // Rule 2: If no root-position match, analyze upper structures as a potential slash chord.
    let pass2Result: string | null = null;
    if (sortedNotes.length > 2) {
        const upperStructureNotes = sortedNotes.slice(1);
        const upperPitchClasses = [...new Set(upperStructureNotes.map(note => note % 12))].sort((a,b)=> a-b);
        let upperChordMatch: {root: string, quality: string} | null = null;
        
        for (let i = 0; i < upperPitchClasses.length; i++) {
            const potentialRoot = upperPitchClasses[i];
            const intervals = upperPitchClasses.map(pc => (pc - potentialRoot + 12) % 12).sort((a, b) => a - b);
            const intervalKey = intervals.join(',');

            if (INTERVALS_TO_CHORD[intervalKey]) {
                const rootNoteName = SHARP_NOTES[potentialRoot];
                const chordQuality = INTERVALS_TO_CHORD[intervalKey].name;
                const upperBassNote = SHARP_NOTES[upperStructureNotes[0] % 12];
                // Prefer root position for the upper structure
                if (rootNoteName === upperBassNote) {
                    upperChordMatch = { root: rootNoteName, quality: chordQuality };
                    break; 
                }
                if (!upperChordMatch) {
                    upperChordMatch = { root: rootNoteName, quality: chordQuality };
                }
            }
        }

        if (upperChordMatch) {
             if (upperChordMatch.root === bassNoteName && upperChordMatch.quality === '') {
                 pass2Result = bassNoteName;
             } else {
                 pass2Result = `${upperChordMatch.root}${upperChordMatch.quality}/${bassNoteName}`;
             }
        }
    }
    
    // Rule 3: If an upper structure analysis provides a result, it's a good candidate.
    if (pass2Result) {
        return pass2Result;
    }

    // Rule 4: As a last resort, use the first inverted chord found in the initial analysis.
    if (possibleMatches.length > 0) {
        const bestInversion = possibleMatches[0];
        return `${bestInversion.root}${bestInversion.quality}/${bassNoteName}`;
    }

    return null; // No interpretation found
};